import { useCallback } from 'react';
import { ApplicationModel } from '../types';
import {
  isPasswordValid,
  isUserNameContainedInPassword,
  doesPasswordContainForbiddenKeywords,
} from '@tradingblock/components';
import { UserRequirementsInfo } from '@tradingblock/types';

interface ValidationErrors {
  [key: string]: string;
}

export const useFormValidation = () => {
  const validatePersonalInfo = useCallback((values: ApplicationModel): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!(values.primaryAccountHolder && values.primaryAccountHolder.firstName)) {
      errors['primaryAccountHolder.firstName'] = 'First name is required';
    }

    if (!(values.primaryAccountHolder && values.primaryAccountHolder.lastName)) {
      errors['primaryAccountHolder.lastName'] = 'Last name is required';
    }

    if (!(values.primaryAccountHolder && values.primaryAccountHolder.email)) {
      errors['primaryAccountHolder.email'] = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.primaryAccountHolder.email)) {
      errors['primaryAccountHolder.email'] = 'Please enter a valid email address';
    }

    if (!(values.primaryAccountHolder && values.primaryAccountHolder.emailConfirm)) {
      errors['primaryAccountHolder.emailConfirm'] = 'Please confirm your email address';
    } else if (
      values.primaryAccountHolder &&
      values.primaryAccountHolder.email &&
      values.primaryAccountHolder.email !== values.primaryAccountHolder.emailConfirm
    ) {
      errors['primaryAccountHolder.emailConfirm'] = 'Email confirmation does not match';
    }

    return errors;
  }, []);

  const validateLoginSecurity = useCallback(
    (values: ApplicationModel, userRequirements?: UserRequirementsInfo): ValidationErrors => {
      const errors: ValidationErrors = {};

      // Username validation
      if (!values.userName) {
        errors.userName = 'Username is required';
      } else if (values.userName.length < 6) {
        errors.userName = 'Username must be at least 6 characters';
      } else if (values.userName.length > 30) {
        errors.userName = 'Username must be no more than 30 characters';
      } else if (!/^[a-zA-Z0-9_-]+$/.test(values.userName)) {
        errors.userName = 'Username can only contain letters, numbers, underscores, and hyphens';
      }

      // Password validation
      if (!values.password) {
        errors.password = 'Password is required';
      } else {
        // Use comprehensive password validation if requirements are available
        if (userRequirements) {
          if (!isPasswordValid(values.password, userRequirements, values.userName)) {
            errors.password = 'Password does not meet security requirements';
          }
        } else {
          // Fallback to basic validation
          if (values.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
          } else if (!/[a-z]/.test(values.password)) {
            errors.password = 'Password must contain at least one lowercase letter';
          } else if (!/[A-Z]/.test(values.password)) {
            errors.password = 'Password must contain at least one uppercase letter';
          } else if (!/[0-9]/.test(values.password)) {
            errors.password = 'Password must contain at least one number';
          } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(values.password)) {
            errors.password = 'Password must contain at least one special character';
          }
        }

        // Additional password checks
        if (values.userName && isUserNameContainedInPassword(values.password, values.userName)) {
          errors.password = 'Password cannot contain your username';
        }

        if (doesPasswordContainForbiddenKeywords(values.password)) {
          errors.password = 'Password contains forbidden keywords';
        }

        // Check if password contains first or last name
        const firstName = values.primaryAccountHolder && values.primaryAccountHolder.firstName;
        const lastName = values.primaryAccountHolder && values.primaryAccountHolder.lastName;

        if (firstName && values.password && values.password.toLowerCase().includes(firstName.toLowerCase())) {
          errors.password = 'Password cannot contain your first name';
        }

        if (lastName && values.password && values.password.toLowerCase().includes(lastName.toLowerCase())) {
          errors.password = 'Password cannot contain your last name';
        }
      }

      // Password confirmation validation
      if (!values.passwordConfirm) {
        errors.passwordConfirm = 'Please confirm your password';
      } else if (values.password !== values.passwordConfirm) {
        errors.passwordConfirm = 'Passwords do not match';
      }

      // Security questions validation
      if (values.securityChallenges) {
        values.securityChallenges.forEach((challenge, index) => {
          if (!challenge.securityQuestionType) {
            errors[`securityChallenges[${index}].securityQuestionType`] = 'Please select a security question';
          }
          if (!challenge.answer) {
            errors[`securityChallenges[${index}].answer`] = 'Please provide an answer';
          } else if (challenge.answer.length < 2) {
            errors[`securityChallenges[${index}].answer`] = 'Answer must be at least 2 characters';
          }
        });
      }

      return errors;
    },
    []
  );

  const validateUserCredentials = useCallback(
    (values: ApplicationModel, userRequirements?: UserRequirementsInfo): ValidationErrors => {
      return {
        ...validatePersonalInfo(values),
        ...validateLoginSecurity(values, userRequirements),
      };
    },
    [validatePersonalInfo, validateLoginSecurity]
  );

  const validateStep = useCallback(
    (values: ApplicationModel, currentStep: number, userRequirements?: UserRequirementsInfo): ValidationErrors => {
      if (currentStep === 0) {
        return validatePersonalInfo(values);
      } else if (currentStep === 1) {
        return validateLoginSecurity(values, userRequirements);
      }
      return {};
    },
    [validatePersonalInfo, validateLoginSecurity]
  );

  return {
    validatePersonalInfo,
    validateLoginSecurity,
    validateUserCredentials,
    validateStep,
  };
};

export default useFormValidation;
